import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import type {
  RequestType,
  CreateDASTicketRequest,
  WorkflowType,
  ApprovalFlowType,
  ApproverEntity,
  ApproverAssignment,
  ApprovalLevelConfig,
  EnhancedAttachment,
  PredefinedWorkflow,
  CreateRequestRequest,
  RequestPriority,
  ID,
  RequestTypeEntity, // Use RequestTypeEntity to avoid conflict with RequestType string union
} from 'api/types';
import { useCreateRequest, useActiveRequestTypes, useActiveWorkflowTemplates, useMyRequests } from 'hooks/api';
import {
  useFileUpload,
  useFileUploadSettings,
  formatFileSize,
} from 'hooks/api/das/useAttachments';
import { useApprovers } from 'hooks/api/das/useApprovers';
import MessageDialog, { type MessageVariant } from '../../common/MessageDialog';
import type { ApproverDto } from 'api/types/das/approvers.types';

/**
 * ============================================================================
 * NEW TICKET MODAL - DIGITAL APPROVAL SYSTEM (COMPREHENSIVE V2 - TABBED)
 * ============================================================================
 *
 * Features:
 * ✅ Tabbed interface for better organization
 * ✅ Workflow/General selection
 * ✅ Request type, priority, subject, description, value
 * ✅ Predefined workflow or custom approval flow
 * ✅ Parallel vs Sequential approval flow
 * ✅ Searchable approver table (User Name, Role, Department, Branch)
 * ✅ Multi-select approvers with role-based approval support
 * ✅ Visual approval flow preview
 * ✅ Link related tickets (searchable)
 * ✅ Enhanced file attachments with title/description
 * ✅ SLA/Time limit configuration
 * ✅ Comprehensive validation
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Extended ticket form data type with real API fields
 * - Uses RequestPriority instead of TicketPriority (aligned with backend)
 * - Adds requestTypeId and requiredByDate for API integration
 */
export type TicketFormData = Omit<CreateDASTicketRequest, 'priority'> & {
  requestTypeId?: ID;
  requiredByDate?: string;
  priority: RequestPriority;
  requiresTwoLevelApproval?: boolean; // Flag for two-level approval workflow
  preApprover?: ApproverAssignment; // Pre-approver for two-level approval workflow
};

export interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: TicketFormData) => void;
  onSuccess?: () => void; // Called after successful API request creation
  predefinedWorkflows?: PredefinedWorkflow[];
}

interface FormErrors {
  requestType?: string;
  subject?: string;
  description?: string;
  value?: string;
  attachments?: string;
  approvalFlow?: string;
}

// ============================================================================
// CONSTANTS & MOCK DATA
// ============================================================================

const REQUEST_TYPES: RequestType[] = [
  'Purchase Request',
  'Leave Request',
  'Travel Request',
  'Budget Approval',
  'Policy Exception',
  'General Request',
];

// Priority options (aligned with backend validation)
const PRIORITIES: RequestPriority[] = ['Normal', 'Medium', 'High', 'Urgent'];

/**
 * Maps ApproverDto from API to ApproverEntity format used by the component
 */
const mapApproverDtoToEntity = (dto: ApproverDto): ApproverEntity => ({
  id: dto.id,
  name: dto.name,
  type: dto.type.toLowerCase() as 'user' | 'role',
  role: dto.role,
  department: dto.department,
  branch: dto.department, // Use department as branch since API doesn't have branch
  email: dto.email,
  userCode: dto.userCode,
  userCount: dto.userCount,
});

// Mock Predefined Workflows
const DEFAULT_WORKFLOWS: PredefinedWorkflow[] = [
  {
    id: 'wf-1',
    name: 'Standard Purchase Approval',
    description: 'Manager → Director → CFO',
    category: 'Purchase',
    steps: [
      { level: 1, approverName: 'Department Manager', approverEmail: '', role: 'Manager' },
      { level: 2, approverName: 'Director', approverEmail: '', role: 'Director' },
      { level: 3, approverName: 'CFO', approverEmail: '', role: 'CFO' },
    ],
  },
  {
    id: 'wf-2',
    name: 'Leave Approval',
    description: 'Manager → HR',
    category: 'Leave',
    steps: [
      { level: 1, approverName: 'Department Manager', approverEmail: '', role: 'Manager' },
      { level: 2, approverName: 'HR Manager', approverEmail: '', role: 'HR' },
    ],
  },
];

// ============================================================================
// SEARCHABLE APPROVER TABLE COMPONENT
// ============================================================================

interface SearchableApproverTableProps {
  approvers: ApproverEntity[];
  onAddSelected: (selected: ApproverEntity[]) => void;
  onClose: () => void;
  excludeIds?: string[];
}

const SearchableApproverTable: React.FC<SearchableApproverTableProps> = ({
  approvers,
  onAddSelected,
  onClose,
  excludeIds = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<keyof ApproverEntity>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const pageSize = 10;

  const filteredApprovers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return approvers.filter(
      (approver) =>
        !excludeIds.includes(approver.id) &&
        (approver.name.toLowerCase().includes(searchLower) ||
          approver.userCode?.toLowerCase().includes(searchLower) ||
          approver.role?.toLowerCase().includes(searchLower) ||
          approver.department?.toLowerCase().includes(searchLower) ||
          approver.branch?.toLowerCase().includes(searchLower))
    );
  }, [approvers, searchTerm, excludeIds]);

  const sortedApprovers = useMemo(() => {
    return [...filteredApprovers].sort((a, b) => {
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredApprovers, sortColumn, sortDirection]);

  const paginatedApprovers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedApprovers.slice(start, start + pageSize);
  }, [sortedApprovers, currentPage]);

  const totalPages = Math.ceil(sortedApprovers.length / pageSize);

  const handleSort = (column: keyof ApproverEntity) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === paginatedApprovers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedApprovers.map((a) => a.id));
    }
  };

  const handleAddSelected = () => {
    const selected = approvers.filter((a) => selectedIds.includes(a.id));
    onAddSelected(selected);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10100 }}>
      <div style={{ background: 'white', borderRadius: '0.5rem', width: '90%', maxWidth: '900px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Select Approvers</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search by name, role, department, or branch..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ border: '1px solid #334155', width: '100%' }}
          />
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === paginatedApprovers.length && paginatedApprovers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                  User/Role Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('role')}>
                  Role {sortColumn === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('department')}>
                  Department {sortColumn === 'department' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('branch')}>
                  Branch {sortColumn === 'branch' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedApprovers.map((approver) => (
                <tr key={approver.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(approver.id)}
                      onChange={() => handleToggleSelect(approver.id)}
                    />
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 500 }}>
                        {approver.name}
                        {approver.userCode && (
                          <span style={{ marginLeft: '0.375rem', color: '#64748b', fontWeight: 400 }}>
                            ({approver.userCode})
                          </span>
                        )}
                      </span>
                      {approver.type === 'role' && (
                        <span style={{ fontSize: '0.75rem', background: '#dbeafe', color: '#1e40af', padding: '0.125rem 0.5rem', borderRadius: '0.25rem' }}>
                          Role – Any one can approve
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', color: '#64748b' }}>{approver.role || '—'}</td>
                  <td style={{ padding: '0.75rem', color: '#64748b' }}>{approver.department || '—'}</td>
                  <td style={{ padding: '0.75rem', color: '#64748b' }}>{approver.branch || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedApprovers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              No approvers found matching your search
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
            {selectedIds.length} approver(s) selected
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// APPROVAL FLOW PREVIEW COMPONENT
// ============================================================================

interface ApprovalFlowPreviewProps {
  flowType: ApprovalFlowType;
  parallelApprovers?: ApproverAssignment[];
  parallelFinalApprover?: ApproverAssignment;
  sequentialLevels?: ApprovalLevelConfig[];
  sequentialFinalApprover?: ApproverAssignment;
  preApprover?: ApproverAssignment;
  requiresTwoLevelApproval?: boolean;
}

const ApprovalFlowPreview: React.FC<ApprovalFlowPreviewProps> = ({
  flowType,
  parallelApprovers,
  parallelFinalApprover,
  sequentialLevels,
  sequentialFinalApprover,
  preApprover,
  requiresTwoLevelApproval,
}) => {
  if (flowType === 'parallel') {
    return (
      <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '1rem' }}>Approval Flow Preview</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Parallel Approvers Block */}
          <div style={{ flex: 1, minWidth: '200px', padding: '1rem', background: 'white', border: '2px solid #3b82f6', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', marginBottom: '0.5rem' }}>
              PARALLEL APPROVAL (All must approve)
            </div>
            {parallelApprovers && parallelApprovers.length > 0 ? (
              parallelApprovers.map((approver) => (
                <div key={approver.id} style={{ fontSize: '0.85rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconSystem.user size={14} />
                  <span>{approver.entityName}</span>
                  {approver.isAnyOneApprove && <span style={{ fontSize: '0.7rem', color: '#64748b' }}>(Any one)</span>}
                </div>
              ))
            ) : (
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>Add approvers below</div>
            )}
          </div>

          {/* Arrow */}
          {parallelFinalApprover && (
            <>
              <IconSystem.arrowRight size={24} style={{ color: '#64748b' }} />
              {/* Final Approver Block */}
              <div style={{ padding: '1rem', background: 'white', border: '2px solid #10b981', borderRadius: '0.5rem', minWidth: '200px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', marginBottom: '0.5rem' }}>
                  FINAL APPROVER
                </div>
                <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconSystem.user size={14} />
                  <span>{parallelFinalApprover.entityName}</span>
                  {parallelFinalApprover.isAnyOneApprove && <span style={{ fontSize: '0.7rem', color: '#64748b' }}>(Any one)</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Sequential Flow - Filter to only show levels with approvers
  const levelsWithApprovers = sequentialLevels?.filter(level => level.approvers.length > 0) || [];
  const hasAnyApprovers = levelsWithApprovers.length > 0 || (requiresTwoLevelApproval && preApprover) || sequentialFinalApprover;

  return (
    <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
      <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '1rem' }}>Approval Flow Preview</h4>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', overflowX: 'auto' }}>
        {hasAnyApprovers ? (
          <>
            {levelsWithApprovers.map((level, index) => (
              <React.Fragment key={level.id}>
                {/* Level Block - Only show levels with approvers */}
                <div style={{ minWidth: '180px', padding: '1rem', background: 'white', border: '2px solid #8b5cf6', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b5cf6', marginBottom: '0.5rem' }}>
                    LEVEL {level.levelNumber}: {level.levelName}
                  </div>
                  {level.approvers.map((approver) => (
                    <div key={approver.id} style={{ fontSize: '0.85rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <IconSystem.user size={14} />
                      <span>{approver.entityName}</span>
                    </div>
                  ))}
                </div>

                {/* Arrow to next level, pre-approver, or final approver */}
                {(index < levelsWithApprovers.length - 1 || (requiresTwoLevelApproval && preApprover) || sequentialFinalApprover) && (
                  <IconSystem.arrowRight size={24} style={{ color: '#64748b' }} />
                )}
              </React.Fragment>
            ))}

            {/* Pre-Approver (Two-Level Approval) - positioned before Final Approver */}
            {requiresTwoLevelApproval && preApprover && (
              <>
                <div style={{ minWidth: '180px', padding: '1rem', background: 'white', border: '2px solid #f59e0b', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.5rem' }}>
                    PRE-APPROVER
                  </div>
                  <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconSystem.user size={14} />
                    <span>{preApprover.entityName}</span>
                  </div>
                </div>
                {sequentialFinalApprover && (
                  <IconSystem.arrowRight size={24} style={{ color: '#64748b' }} />
                )}
              </>
            )}

            {/* Final Approver */}
            {sequentialFinalApprover && (
              <div style={{ minWidth: '180px', padding: '1rem', background: 'white', border: '2px solid #10b981', borderRadius: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', marginBottom: '0.5rem' }}>
                  FINAL APPROVER
                </div>
                <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconSystem.user size={14} />
                  <span>{sequentialFinalApprover.entityName}</span>
                  {sequentialFinalApprover.isAnyOneApprove && <span style={{ fontSize: '0.7rem', color: '#64748b' }}>(Any one)</span>}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic', padding: '1rem' }}>
            Add approvers to see the approval flow
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

export const NewTicketModal: React.FC<NewTicketModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onSuccess,
  predefinedWorkflows = DEFAULT_WORKFLOWS,
}) => {
  // API hooks
  const { createRequest, loading: creatingRequest, error: createError } = useCreateRequest();
  const { requestTypes, loading: loadingRequestTypes, error: requestTypesError, refetch: refetchRequestTypes } = useActiveRequestTypes() as {
    requestTypes: RequestTypeEntity[];
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
  };
  const { templates: workflowTemplates, loading: loadingWorkflowTemplates, error: workflowTemplatesError } = useActiveWorkflowTemplates();
  // Fetch file upload settings from API (falls back to defaults if unavailable)
  const { limits: fileUploadLimits } = useFileUploadSettings();
  // Fetch approvers only when modal opens (not on component mount)
  const { approvers: fetchedApprovers, loading: loadingApprovers, error: approversError, refetch: refetchApprovers } = useApprovers({ isActive: true }, false);
  const { data: fetchedRequests, loading: loadingRequests } = useMyRequests();

  // Map fetched approvers to ApproverEntity format
  const activeApprovers: ApproverEntity[] = useMemo(() => {
    return fetchedApprovers.map(mapApproverDtoToEntity);
  }, [fetchedApprovers]);

  // Map WorkflowTemplateDto to PredefinedWorkflow format with extended fields
  const mappedWorkflowTemplates: (PredefinedWorkflow & {
    applicableRequestTypes?: string[];
    requiresSequential?: boolean;
    allowParallel?: boolean;
    minimumApprovers?: number;
    isDefault?: boolean;
  })[] = useMemo(() => {
    return workflowTemplates.map((template) => ({
      id: template.id,
      name: template.workflowTemplateName,
      description: template.description || '',
      category: template.category || '',
      steps: template.steps.map((step) => ({
        level: step.level,
        approverName: step.approverRoleName || step.approverUserName || '',
        approverEmail: step.approverEmail || '',
        role: step.approverRoleName || '',
        approvalType: step.approvalType,
        isRequired: step.isRequired,
        allowDelegation: step.allowDelegation,
      })),
      // Extended fields for filtering and display
      applicableRequestTypes: template.applicableRequestTypes,
      requiresSequential: template.requiresSequential,
      allowParallel: template.allowParallel,
      minimumApprovers: template.minimumApprovers,
      isDefault: template.isDefault,
    }));
  }, [workflowTemplates]);

  // Use fetched templates if available, otherwise fall back to prop or default
  const activeWorkflows = mappedWorkflowTemplates.length > 0 ? mappedWorkflowTemplates : predefinedWorkflows;

  // Form state (uses TicketFormData type with RequestPriority for real API)
  const [formData, setFormData] = useState<TicketFormData>({
    workflowType: 'general', // Default to Custom Approval Flow
    requestType: 'General Request',
    requestTypeId: undefined, // Will be set when request types load
    priority: 'Normal',
    subject: '',
    description: '',
    value: undefined,
    requiredByDate: undefined,
    predefinedWorkflowId: undefined,
    approvalFlowType: 'sequential', // Default to Sequential Approval
    parallelApprovers: [],
    parallelFinalApprover: undefined,
    sequentialLevels: [],
    sequentialFinalApprover: undefined,
    relatedTickets: [],
    attachments: [],
    slaHours: undefined,
    requiresTwoLevelApproval: false, // Two-level approval workflow flag
    preApprover: undefined, // Pre-approver for two-level approval
  });

  // Compute effective file upload limits based on selected request type (overrides global)
  const selectedRequestType = useMemo(() => {
    return requestTypes.find((rt: RequestTypeEntity) => rt.id === formData.requestTypeId) || null;
  }, [requestTypes, formData.requestTypeId]);

  const effectiveFileLimits = useMemo(() => {
    if (!selectedRequestType) return fileUploadLimits;

    return {
      maxIndividualSize: selectedRequestType.maxFileSizeKB
        ? selectedRequestType.maxFileSizeKB * 1024
        : fileUploadLimits.maxIndividualSize,
      maxTotalSize: selectedRequestType.maxTotalSizeKB
        ? selectedRequestType.maxTotalSizeKB * 1024
        : fileUploadLimits.maxTotalSize,
      allowedExtensions: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions
        : fileUploadLimits.allowedExtensions,
      allowedExtensionsDisplay: selectedRequestType.allowedExtensions
        ? selectedRequestType.allowedExtensions.join(', ')
        : fileUploadLimits.allowedExtensionsDisplay,
    };
  }, [selectedRequestType, fileUploadLimits]);

  const { uploadFile, validateFile, getFormattedTotalSize, fileSizeLimits } = useFileUpload(effectiveFileLimits);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Centered in-app message dialog (replaces native window.alert, which the
  // browser always pins to the top of the screen).
  const [messageDialog, setMessageDialog] = useState<{
    variant: MessageVariant;
    title: string;
    message: string;
    onOk: () => void;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // UI state
  const [showApproverTable, setShowApproverTable] = useState(false);
  const [approverTableContext, setApproverTableContext] = useState<{
    type: 'parallel' | 'parallel-final' | 'sequential-level' | 'sequential-final' | 'pre-approver';
    levelId?: string;
  } | null>(null);
  const [relatedTicketsSearch, setRelatedTicketsSearch] = useState('');

  // Preview state for viewing attachments
  const [previewAttachment, setPreviewAttachment] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  const [collapsedLevels, setCollapsedLevels] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'basic' | 'attachments' | 'workflow' | 'related' | 'sla'>('basic');

  // Request Type dropdown state
  const [requestTypeSearch, setRequestTypeSearch] = useState('');
  const [requestTypeDropdownOpen, setRequestTypeDropdownOpen] = useState(false);

  // Ref for modal content scroll container
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when tab changes
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Fetch approvers and request types when modal opens
  useEffect(() => {
    if (isOpen) {
      refetchApprovers();
      refetchRequestTypes();
    }
  }, [isOpen, refetchApprovers, refetchRequestTypes]);

  // Filtered request types based on search
  const filteredRequestTypes = useMemo(() => {
    if (!requestTypeSearch.trim()) {
      return requestTypes;
    }
    const searchLower = requestTypeSearch.toLowerCase();
    return requestTypes.filter((rt: RequestTypeEntity) =>
      rt.requestTypeName.toLowerCase().includes(searchLower) ||
      rt.requestTypeCode?.toLowerCase().includes(searchLower)
    );
  }, [requestTypes, requestTypeSearch]);

  // Filter workflows by selected request type (if applicable)
  const filteredWorkflows = useMemo(() => {
    if (!selectedRequestType || mappedWorkflowTemplates.length === 0) {
      return activeWorkflows;
    }

    // Filter workflows that are applicable to this request type
    const filtered = mappedWorkflowTemplates.filter(wf => {
      // If no applicableRequestTypes defined, show to all
      if (!wf.applicableRequestTypes || wf.applicableRequestTypes.length === 0) {
        return true;
      }
      // Check if request type code matches
      return wf.applicableRequestTypes.includes(selectedRequestType.requestTypeCode);
    });

    // If no filtered results, fall back to showing all
    return filtered.length > 0 ? filtered : activeWorkflows;
  }, [selectedRequestType, mappedWorkflowTemplates, activeWorkflows]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Request type validation
    if (!formData.requestTypeId) {
      newErrors.requestType = 'Please select a request type';
    }

    // Basic validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 10000) {
      newErrors.description = 'Description cannot exceed 10000 characters';
    }

    // Value validation - required by request type
    if (selectedRequestType?.requiresValue) {
      if (formData.value === undefined || formData.value === null || formData.value === 0) {
        newErrors.value = `Value is required for "${selectedRequestType.requestTypeName}" requests`;
      } else if (isNaN(formData.value) || formData.value < 0) {
        newErrors.value = 'Value must be a valid positive number';
      }
    } else if (formData.value !== undefined) {
      if (isNaN(formData.value) || formData.value < 0) {
        newErrors.value = 'Value must be a valid positive number';
      }
    }

    // Attachment validation - required by request type
    if (selectedRequestType?.requiresAttachment) {
      if (!formData.attachments || formData.attachments.length === 0) {
        newErrors.attachments = `At least one attachment is required for "${selectedRequestType.requestTypeName}" requests`;
      }
    }

    // Validate all attachments have titles
    if (formData.attachments && formData.attachments.length > 0) {
      const emptyTitleIndex = formData.attachments.findIndex(a => !a.title.trim());
      if (emptyTitleIndex !== -1) {
        newErrors.attachments = `Attachment ${emptyTitleIndex + 1} is missing a title`;
      }
    }

    // Pre-Approver validation (Two-Level Approval)
    if (formData.requiresTwoLevelApproval && !formData.preApprover) {
      newErrors.approvalFlow = 'Pre-Approver is required when Pre-Approval Required is enabled';
    }

    // Approval flow validation
    if (!newErrors.approvalFlow) {
      if (formData.workflowType === 'workflow') {
        if (!formData.predefinedWorkflowId) {
          newErrors.approvalFlow = 'Please select a predefined workflow';
        }
      } else if (formData.workflowType === 'general') {
        if (formData.approvalFlowType === 'parallel') {
          if (!formData.parallelApprovers || formData.parallelApprovers.length === 0) {
            newErrors.approvalFlow = 'Please add at least one approver for parallel approval';
          }
        } else if (formData.approvalFlowType === 'sequential') {
          // Check if at least one level has approvers OR final approver is set
          const hasLevelApprovers = formData.sequentialLevels?.some((level) => level.approvers.length > 0);
          const hasFinalApprover = !!formData.sequentialFinalApprover;

          if (!hasLevelApprovers && !hasFinalApprover) {
            newErrors.approvalFlow = 'Please add at least one approver (level approvers or final approver)';
          }
        }
      }
    }

    setErrors(newErrors);

    // Switch to the tab with errors
    if (newErrors.requestType) {
      setActiveTab('basic');
    } else if (newErrors.subject || newErrors.description || newErrors.value) {
      setActiveTab('basic');
    } else if (newErrors.attachments) {
      setActiveTab('attachments');
    } else if (newErrors.approvalFlow) {
      setActiveTab('workflow');
    }

    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // EVENT HANDLERS (... keeping all the existing handlers ...)
  // ============================================================================

  const handleInputChange = useCallback((field: keyof TicketFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleWorkflowTypeChange = useCallback((type: WorkflowType) => {
    setFormData((prev) => ({
      ...prev,
      workflowType: type,
      predefinedWorkflowId: type === 'workflow' ? prev.predefinedWorkflowId : undefined,
      approvalFlowType: type === 'general' ? (prev.approvalFlowType || 'parallel') : undefined,
    }));
    setErrors((prev) => ({ ...prev, approvalFlow: undefined }));
  }, []);

  const handleApprovalFlowTypeChange = useCallback((type: ApprovalFlowType) => {
    setFormData((prev) => ({
      ...prev,
      approvalFlowType: type,
      ...(type === 'parallel'
        ? { sequentialLevels: [], sequentialFinalApprover: undefined }
        : { parallelApprovers: [], parallelFinalApprover: undefined }),
    }));
    setErrors((prev) => ({ ...prev, approvalFlow: undefined }));
  }, []);

  const openApproverTable = useCallback(
    (context: { type: 'parallel' | 'parallel-final' | 'sequential-level' | 'sequential-final' | 'pre-approver'; levelId?: string }) => {
      setApproverTableContext(context);
      setShowApproverTable(true);
    },
    []
  );

  const handleApproversSelected = useCallback(
    (selected: ApproverEntity[]) => {
      if (!approverTableContext) return;

      const assignments: ApproverAssignment[] = selected.map((entity) => ({
        id: `assignment-${Date.now()}-${Math.random()}`,
        entityId: entity.id,
        entityName: entity.name,
        entityType: entity.type,
        role: entity.role,
        department: entity.department,
        branch: entity.branch,
        email: entity.email,
        isAnyOneApprove: entity.type === 'role',
      }));

      setFormData((prev) => {
        switch (approverTableContext.type) {
          case 'parallel':
            return {
              ...prev,
              parallelApprovers: [...(prev.parallelApprovers || []), ...assignments],
            };

          case 'parallel-final':
            return {
              ...prev,
              parallelFinalApprover: assignments[0],
            };

          case 'sequential-level':
            if (!approverTableContext.levelId) return prev;
            return {
              ...prev,
              sequentialLevels: prev.sequentialLevels?.map((level) =>
                level.id === approverTableContext.levelId
                  ? { ...level, approvers: [...level.approvers, ...assignments] }
                  : level
              ),
            };

          case 'sequential-final':
            return {
              ...prev,
              sequentialFinalApprover: assignments[0],
            };

          case 'pre-approver':
            return {
              ...prev,
              preApprover: assignments[0],
            };

          default:
            return prev;
        }
      });

      setErrors((prev) => ({ ...prev, approvalFlow: undefined }));
    },
    [approverTableContext]
  );

  const handleRemoveApprover = useCallback(
    (context: 'parallel' | 'parallel-final' | 'sequential-final' | 'pre-approver', assignmentId?: string) => {
      setFormData((prev) => {
        switch (context) {
          case 'parallel':
            return {
              ...prev,
              parallelApprovers: prev.parallelApprovers?.filter((a) => a.id !== assignmentId),
            };

          case 'parallel-final':
            return {
              ...prev,
              parallelFinalApprover: undefined,
            };

          case 'sequential-final':
            return {
              ...prev,
              sequentialFinalApprover: undefined,
            };

          case 'pre-approver':
            return {
              ...prev,
              preApprover: undefined,
            };

          default:
            return prev;
        }
      });
    },
    []
  );

  const handleRemoveApproverFromLevel = useCallback((levelId: string, assignmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      sequentialLevels: prev.sequentialLevels?.map((level) =>
        level.id === levelId
          ? { ...level, approvers: level.approvers.filter((a) => a.id !== assignmentId) }
          : level
      ),
    }));
  }, []);

  const handleAddSequentialLevel = useCallback(() => {
    const newLevel: ApprovalLevelConfig = {
      id: `level-${Date.now()}`,
      levelNumber: (formData.sequentialLevels?.length || 0) + 1,
      levelName: `Level ${(formData.sequentialLevels?.length || 0) + 1}`,
      approvers: [],
      requireAllApprovals: true,
    };

    // Collapse all existing levels, keep only the new level expanded
    if (formData.sequentialLevels && formData.sequentialLevels.length > 0) {
      const existingLevelIds = formData.sequentialLevels.map((level) => level.id);
      setCollapsedLevels(new Set(existingLevelIds));
    }

    setFormData((prev) => ({
      ...prev,
      sequentialLevels: [...(prev.sequentialLevels || []), newLevel],
    }));
  }, [formData.sequentialLevels]);

  const handleRemoveSequentialLevel = useCallback((levelId: string) => {
    setFormData((prev) => ({
      ...prev,
      sequentialLevels: prev.sequentialLevels
        ?.filter((level) => level.id !== levelId)
        .map((level, index) => ({
          ...level,
          levelNumber: index + 1,
          levelName: `Level ${index + 1}`,
        })),
    }));
  }, []);

  const toggleLevelCollapse = useCallback((levelId: string) => {
    setCollapsedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(levelId)) {
        next.delete(levelId);
      } else {
        next.add(levelId);
      }
      return next;
    });
  }, []);

  const handleToggleRelatedTicket = useCallback((ticketId: string) => {
    setFormData((prev) => {
      const relatedTickets = prev.relatedTickets || [];
      const isLinked = relatedTickets.includes(ticketId);
      return {
        ...prev,
        relatedTickets: isLinked
          ? relatedTickets.filter((id) => id !== ticketId)
          : [...relatedTickets, ticketId],
      };
    });
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file extension before accepting
      if (fileSizeLimits.allowedExtensions && fileSizeLimits.allowedExtensions.length > 0) {
        const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
        const allowed = fileSizeLimits.allowedExtensions.map(e => e.toLowerCase());
        if (!allowed.includes(fileExt)) {
          setMessageDialog({
            variant: 'warning',
            title: 'File Type Not Allowed',
            message: `File type "${fileExt}" is not allowed.\nAllowed types: ${fileSizeLimits.allowedExtensionsDisplay || allowed.join(', ')}`,
            onOk: () => setMessageDialog(null),
          });
          event.target.value = '';
          return;
        }
      }

      // Validate file size before accepting
      const existingFiles = formData.attachments?.map((a) => a.file) || [];
      const validation = validateFile(file, existingFiles);

      if (!validation.isValid) {
        setMessageDialog({
          variant: 'warning',
          title: 'File Cannot Be Added',
          message: validation.errorMessage || 'The selected file is not valid.',
          onOk: () => setMessageDialog(null),
        });
        event.target.value = '';
        return;
      }

      // Auto-add to attachments list with filename as default title
      const defaultTitle = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      const enhancedAttachment: EnhancedAttachment = {
        file,
        title: defaultTitle,
        description: '',
      };

      setFormData((prev) => {
        const updatedAttachments = [...(prev.attachments || []), enhancedAttachment];
        console.log('📎 Auto-added attachment. Count:', updatedAttachments.length, 'File:', file.name);
        return { ...prev, attachments: updatedAttachments };
      });

      // Reset file input so user can select another file
      event.target.value = '';
    }
  }, [formData.attachments, validateFile, fileSizeLimits]);

  const handleUpdateAttachment = useCallback((index: number, field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments?.map((att, i) =>
        i === index ? { ...att, [field]: value } : att
      ),
    }));
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index),
    }));
  }, []);

  // View/Preview attachment
  const handleViewAttachment = useCallback((attachment: EnhancedAttachment) => {
    const url = URL.createObjectURL(attachment.file);
    setPreviewAttachment({
      url,
      fileName: attachment.file.name,
      fileType: attachment.file.type,
    });
  }, []);

  // Close preview and cleanup blob URL
  const closePreview = useCallback(() => {
    if (previewAttachment?.url) {
      URL.revokeObjectURL(previewAttachment.url);
    }
    setPreviewAttachment(null);
  }, [previewAttachment]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show confirmation dialog before submitting
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      // If custom onSave handler is provided, use it (for backward compatibility)
      if (onSave) {
        await onSave(formData);
        handleClose();
        return;
      }

      // Map frontend form data to backend CreateRequestRequest
      // DEBUG: Log form data values before mapping
      console.log('📋 FORM DATA DEBUG:', {
        'formData.value': formData.value,
        'formData.requiredByDate': formData.requiredByDate,
        'typeof formData.value': typeof formData.value,
      });

      const requestData: CreateRequestRequest = {
        title: formData.subject,
        description: formData.description || undefined,
        requestTypeId: formData.requestTypeId!, // Validated in validateForm()
        priority: formData.priority, // Direct use - no mapping needed (aligned with backend)
        requestValue: formData.value,
        requiredByDate: formData.requiredByDate || undefined,
        requiresTwoLevelApproval: formData.requiresTwoLevelApproval || false, // Two-level approval flag

        // WORKFLOW CONFIGURATION
        // If using predefined workflow template
        ...(formData.workflowType === 'workflow' && formData.predefinedWorkflowId ? {
          workflowTemplateId: formData.predefinedWorkflowId,
        } : {}),

        // If using custom approval flow (general)
        ...(formData.workflowType === 'general' ? {
          workflowType: 'Custom Approval Flow',
          approvalFlowType: formData.approvalFlowType === 'parallel'
            ? 'Parallel Approval'
            : 'Sequential Approval',

          // Map approvers based on flow type
          approvers: formData.approvalFlowType === 'parallel'
            ? [
                // Regular parallel approvers - all at same level (sequenceOrder: 1)
                ...(formData.parallelApprovers?.map(approver => ({
                  approverType: approver.entityType, // 'user' or 'role'
                  approverUserId: approver.entityType === 'user' ? approver.entityId : undefined,
                  approverRoleId: approver.entityType === 'role' ? approver.entityId : undefined,
                  sequenceOrder: 1, // All parallel approvers at same level
                  levelName: approver.role || 'Approver',
                  isFinalApprover: false,
                  canDelegate: true,
                  isPreApprover: false,
                })) || []),
                // Final approver - comes after all parallel approvers
                ...(formData.parallelFinalApprover ? [{
                  approverType: formData.parallelFinalApprover.entityType,
                  approverUserId: formData.parallelFinalApprover.entityType === 'user'
                    ? formData.parallelFinalApprover.entityId
                    : undefined,
                  approverRoleId: formData.parallelFinalApprover.entityType === 'role'
                    ? formData.parallelFinalApprover.entityId
                    : undefined,
                  sequenceOrder: (formData.parallelApprovers?.length || 0) > 0 ? 2 : 1, // After parallel approvers, or 1 if no parallel approvers
                  levelName: formData.parallelFinalApprover.role || 'Final Approver',
                  isFinalApprover: true,
                  canDelegate: false,
                  isPreApprover: false,
                }] : [])
              ]
            : (() => {
                // Calculate max level number, defaulting to 0 if no levels exist
                // Note: Math.max(...[]) returns -Infinity, so we need to handle empty arrays
                const levelNumbers = formData.sequentialLevels?.map(l => l.levelNumber) || [];
                const maxLevelNumber = levelNumbers.length > 0 ? Math.max(...levelNumbers) : 0;
                const hasPreApprover = formData.requiresTwoLevelApproval && formData.preApprover;

                return [
                  // Sequential level approvers (NOT final approvers)
                  // All approvers in the same level get the SAME sequenceOrder (level.levelNumber)
                  ...(formData.sequentialLevels?.flatMap((level) =>
                    level.approvers.map(approver => ({
                      approverType: approver.entityType,
                      approverUserId: approver.entityType === 'user' ? approver.entityId : undefined,
                      approverRoleId: approver.entityType === 'role' ? approver.entityId : undefined,
                      sequenceOrder: level.levelNumber, // Use actual level number, not array index
                      levelName: level.levelName,
                      isFinalApprover: false, // Level approvers are NOT final approvers
                      canDelegate: true,
                      isPreApprover: false,
                    }))
                  ) || []),
                  // Pre-approver (for two-level approval workflow)
                  // Pre-approver's sequenceOrder = max level number + 1
                  ...(hasPreApprover ? [{
                    approverType: formData.preApprover!.entityType,
                    approverUserId: formData.preApprover!.entityType === 'user'
                      ? formData.preApprover!.entityId
                      : undefined,
                    approverRoleId: formData.preApprover!.entityType === 'role'
                      ? formData.preApprover!.entityId
                      : undefined,
                    sequenceOrder: maxLevelNumber + 1,
                    levelName: 'Pre-Approver',
                    isFinalApprover: false,
                    canDelegate: true,
                    isPreApprover: true, // This is a pre-approver for two-level workflow
                  }] : []),
                  // Sequential final approver (the actual final approver)
                  // Final approver's sequenceOrder = max level number + 1 (or +2 if pre-approver exists)
                  ...(formData.sequentialFinalApprover ? [{
                    approverType: formData.sequentialFinalApprover.entityType,
                    approverUserId: formData.sequentialFinalApprover.entityType === 'user'
                      ? formData.sequentialFinalApprover.entityId
                      : undefined,
                    approverRoleId: formData.sequentialFinalApprover.entityType === 'role'
                      ? formData.sequentialFinalApprover.entityId
                      : undefined,
                    sequenceOrder: maxLevelNumber + (hasPreApprover ? 2 : 1),
                    levelName: 'Final Approver',
                    isFinalApprover: true, // This is THE final approver
                    canDelegate: false,
                    isPreApprover: false,
                  }] : []),
                ];
              })(),

          // Related tickets
          relatedRequestIds: formData.relatedTickets || undefined,
        } : {}),

        // Keep original data in metadata for reference (optional)
        metadata: JSON.stringify({
          requestType: formData.requestType,
          slaHours: formData.slaHours,
        }),
      };

      // DEBUG: Log the request data being sent to API
      console.log('🚀 REQUEST DATA TO API:', {
        requestValue: requestData.requestValue,
        requiredByDate: requestData.requiredByDate,
        fullPayload: JSON.stringify(requestData, null, 2),
      });

      // Call API to create request
      const createdRequest = await createRequest(requestData);
      console.log('✅ Request created successfully:', createdRequest);
      console.log('📊 CREATED REQUEST - Value fields:', {
        requestValue: createdRequest.requestValue,
        requiredByDate: createdRequest.requiredByDate,
      });
      console.log('📎 ATTACHMENT DEBUG:',
        '\n  - formData.attachments exists?', formData.attachments !== undefined && formData.attachments !== null,
        '\n  - Attachment count:', formData.attachments?.length || 0,
        '\n  - Attachments array:', formData.attachments
      );

      // Build the success message shown in the centered dialog below.
      let successVariant: MessageVariant = 'success';
      let successMessage = `Request Number: ${createdRequest.requestNumber}\nTitle: ${createdRequest.title}`;

      // Upload attachments if any
      if (formData.attachments && formData.attachments.length > 0) {
        console.log('🚀 STARTING UPLOAD for', formData.attachments.length, 'files');
        try {
          console.log(`Uploading ${formData.attachments.length} attachment(s) for request ${createdRequest.id}...`);

          // Upload all attachments
          const uploadPromises = formData.attachments.map((attachment) =>
            uploadFile(attachment.file, {
              requestId: createdRequest.id,
              documentTitle: attachment.title,
              documentDescription: attachment.description || undefined,
            })
          );

          const uploadResults = await Promise.allSettled(uploadPromises);

          // Check for failed uploads (rejected promises or fulfilled with null)
          const failedUploads = uploadResults.filter(
            result => result.status === 'rejected' ||
              (result.status === 'fulfilled' && result.value === null)
          );

          if (failedUploads.length > 0) {
            // Extract error messages from rejected promises
            const errorMessages = failedUploads
              .map(result => result.status === 'rejected' ? (result.reason?.message || 'Unknown error') : 'Upload failed')
              .join('; ');
            console.warn(`${failedUploads.length} attachment(s) failed to upload:`, errorMessages);
            successVariant = 'warning';
            successMessage =
              `Request Number: ${createdRequest.requestNumber}\n` +
              `Title: ${createdRequest.title}\n\n` +
              `⚠️ Warning: ${failedUploads.length} of ${formData.attachments.length} attachment(s) failed to upload.\n` +
              `Error: ${errorMessages}`;
          } else {
            console.log('All attachments uploaded successfully');
            successMessage =
              `Request Number: ${createdRequest.requestNumber}\n` +
              `Title: ${createdRequest.title}\n` +
              `Attachments: ${formData.attachments.length} file(s) uploaded`;
          }
        } catch (error) {
          console.error('Error uploading attachments:', error);
          successVariant = 'warning';
          successMessage =
            `Request Number: ${createdRequest.requestNumber}\n` +
            `Title: ${createdRequest.title}\n\n` +
            `⚠️ Warning: Failed to upload attachments.`;
        }
      }

      // Show a centered success dialog; refresh the parent list and close the
      // modal only after the user acknowledges (mirrors the prior blocking
      // alert() flow, but centered instead of pinned to the top of the screen).
      setMessageDialog({
        variant: successVariant,
        title: 'Request Created Successfully',
        message: successMessage,
        onOk: () => {
          setMessageDialog(null);
          if (onSuccess) {
            onSuccess();
          }
          handleClose();
        },
      });
    } catch (error: any) {
      console.error('Error creating request:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      // Extract detailed error message from backend response
      // ASP.NET Core returns: { title: "...", errors: { fieldName: ["error1", "error2"] } }
      // Custom API returns: { success: false, message: "...", errors: [...] }
      const responseData = error.response?.data;
      const backendMessage = responseData?.message || responseData?.title || error.message || 'Unknown error';

      // Handle both array and object error formats
      let errorList: string[] = [];
      if (responseData?.errors) {
        if (Array.isArray(responseData.errors)) {
          // Custom API format: errors is an array
          errorList = responseData.errors;
        } else if (typeof responseData.errors === 'object') {
          // ASP.NET Core format: errors is an object { fieldName: ["error1"] }
          errorList = Object.entries(responseData.errors).flatMap(
            ([field, messages]) => (messages as string[]).map(msg => `${field}: ${msg}`)
          );
        }
      }

      let errorMessage = backendMessage;
      if (errorList.length > 0) {
        errorMessage += `\n\nValidation Errors:\n• ${errorList.join('\n• ')}`;
      }

      setMessageDialog({
        variant: 'error',
        title: 'Failed to Create Request',
        message: errorMessage,
        onOk: () => setMessageDialog(null),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    setFormData({
      workflowType: 'general', // Default to Custom Approval Flow
      requestType: 'General Request',
      requestTypeId: undefined,
      priority: 'Normal',
      subject: '',
      description: '',
      value: undefined,
      requiredByDate: undefined,
      predefinedWorkflowId: undefined,
      approvalFlowType: 'sequential', // Default to Sequential Approval
      parallelApprovers: [],
      parallelFinalApprover: undefined,
      sequentialLevels: [],
      sequentialFinalApprover: undefined,
      relatedTickets: [],
      attachments: [],
      slaHours: undefined,
      requiresTwoLevelApproval: false, // Reset two-level approval flag
      preApprover: undefined, // Reset pre-approver selection
    });
    setErrors({});
    setRelatedTicketsSearch('');
    setCollapsedLevels(new Set());
    setActiveTab('basic'); // Default to Basic Info tab
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  // Map fetched requests to ticket format for Related Tickets selection
  const availableTickets = useMemo(() => {
    return fetchedRequests.map((request) => ({
      id: String(request.id),
      ticketNo: request.requestNumber,
      subject: request.title,
      requestTypeName: request.requestTypeName,
      currentStatus: request.currentStatus,
      createdByName: request.submittedByName,
      createdAt: request.createdAt,
    }));
  }, [fetchedRequests]);

  const filteredTickets = useMemo(
    () =>
      availableTickets.filter((ticket) => {
        const searchLower = relatedTicketsSearch.toLowerCase();
        return (
          ticket.ticketNo.toLowerCase().includes(searchLower) ||
          ticket.subject.toLowerCase().includes(searchLower) ||
          ticket.requestTypeName?.toLowerCase().includes(searchLower)
        );
      }),
    [availableTickets, relatedTicketsSearch]
  );

  // Helper to get status color for related tickets
  const getRelatedTicketStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return '#10b981';
      case 'pre approved':
        return '#06b6d4';
      case 'rejected':
        return '#ef4444';
      case 'pending':
      case 'submitted':
      case 'in review':
        return '#f59e0b';
      case 'draft':
        return '#94a3b8';
      case 'cancelled':
        return '#64748b';
      default:
        return '#6b7280';
    }
  };

  // Helper to format date for related tickets
  const formatRelatedTicketDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getExcludedApproverIds = useCallback((): string[] => {
    const ids: string[] = [];

    // Exclude pre-approver if selected
    if (formData.preApprover) ids.push(formData.preApprover.entityId);

    if (formData.approvalFlowType === 'parallel') {
      formData.parallelApprovers?.forEach((a) => ids.push(a.entityId));
      if (formData.parallelFinalApprover) ids.push(formData.parallelFinalApprover.entityId);
    } else if (formData.approvalFlowType === 'sequential') {
      formData.sequentialLevels?.forEach((level) => {
        level.approvers.forEach((a) => ids.push(a.entityId));
      });
      if (formData.sequentialFinalApprover) ids.push(formData.sequentialFinalApprover.entityId);
    }

    return ids;
  }, [formData]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">Create New Request</h2>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div ref={modalContentRef} className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto', padding: 0 }}>
          <form onSubmit={handleSubmit}>
            {/* Tab Navigation - Reorganized: Basic Info, Attachments, Workflow, SLA */}
            <div className="tab-navigation">
              <button
                type="button"
                className={`tab-button ${activeTab === 'basic' ? 'active' : ''} ${errors.subject || errors.description || errors.value ? 'has-error' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                <IconSystem.file size={16} />
                <span>Basic Info</span>
                {(errors.subject || errors.description || errors.value) && <span className="error-indicator">!</span>}
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'attachments' ? 'active' : ''} ${errors.attachments ? 'has-error' : ''}`}
                onClick={() => setActiveTab('attachments')}
              >
                <IconSystem.upload size={16} />
                <span>Attachments</span>
                {selectedRequestType?.requiresAttachment
                  ? <span style={{ color: '#ef4444', fontSize: '0.7rem', fontWeight: 600 }}>*Required</span>
                  : <span className="optional-label">(Optional)</span>
                }
                {errors.attachments && <span className="error-indicator">!</span>}
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'workflow' ? 'active' : ''} ${errors.approvalFlow ? 'has-error' : ''}`}
                onClick={() => setActiveTab('workflow')}
              >
                <IconSystem.settings size={16} />
                <span>Workflow & Approval</span>
                {errors.approvalFlow && <span className="error-indicator">!</span>}
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'related' ? 'active' : ''}`}
                onClick={() => setActiveTab('related')}
              >
                <IconSystem.link size={16} />
                <span>Related Requests</span>
                <span className="optional-label">(Optional)</span>
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'sla' ? 'active' : ''}`}
                onClick={() => setActiveTab('sla')}
              >
                <IconSystem.clock size={16} />
                <span>Time Limit / SLA</span>
                <span className="optional-label">(Optional)</span>
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Tab 1: Workflow & Approval Configuration */}
              {activeTab === 'workflow' && (
                <div className="tab-panel">
                  {/* Pre-Approval Required Section */}
                  <div className="form-section" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '1.5rem', marginBottom: '1.5rem' }}>
                    <h3 className="form-section-title">Pre-Approval Configuration</h3>

                    {/* Pre-Approval Required Checkbox */}
                    <div className="form-group">
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.requiresTwoLevelApproval || false}
                          onChange={(e) => handleInputChange('requiresTwoLevelApproval', e.target.checked)}
                          disabled={isSubmitting}
                          style={{
                            width: '1.125rem',
                            height: '1.125rem',
                            accentColor: '#10b981',
                          }}
                        />
                        <span style={{ fontWeight: 500 }}>Pre-Approval Required</span>
                      </label>
                      <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem', marginLeft: '1.625rem' }}>
                        Enable this if the request requires pre-approval before going through the standard approval workflow.
                        Pre-approved requests will be consolidated for final approval.
                      </p>
                    </div>

                    {/* Pre-Approver Selection - shown when Pre-Approval Required is checked */}
                    {formData.requiresTwoLevelApproval && (
                      <div className="form-group" style={{ marginTop: '1rem', marginLeft: '1.625rem' }}>
                        <label className="form-label">
                          Select Pre-Approver <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        {formData.preApprover ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.75rem',
                              background: '#fffbeb',
                              borderRadius: '0.375rem',
                              border: '1px solid #fde68a',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>{formData.preApprover.entityName}</div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {formData.preApprover.role} {formData.preApprover.department ? `• ${formData.preApprover.department}` : ''} {formData.preApprover.branch ? `• ${formData.preApprover.branch}` : ''}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => handleRemoveApprover('pre-approver')}
                              disabled={isSubmitting}
                              title="Remove pre-approver"
                            >
                              <IconSystem.trash size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => openApproverTable({ type: 'pre-approver' })}
                            disabled={isSubmitting}
                          >
                            <IconSystem.add size={16} />
                            Add Pre-Approver
                          </button>
                        )}
                        {approversError && (
                          <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            Error loading approvers.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Sequential Approval Workflow */}
                  <div className="form-section" style={{ borderBottom: 'none' }}>
                    <h3 className="form-section-title">
                      Sequential Approval Levels
                    </h3>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
                      Configure the approval levels. Each level must be completed before moving to the next.
                    </p>

                    {/* Sequential Levels */}
                    <div className="form-group">
                      <label className="form-label">
                        Approval Levels <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleAddSequentialLevel}
                        disabled={isSubmitting}
                        style={{ marginBottom: '0.75rem' }}
                      >
                        <IconSystem.add size={16} />
                        Add Approval Level
                      </button>

                      {formData.sequentialLevels && formData.sequentialLevels.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {formData.sequentialLevels.map((level) => (
                            <div
                              key={level.id}
                              style={{
                                border: '2px solid #8b5cf6',
                                borderRadius: '0.5rem',
                                overflow: 'hidden',
                              }}
                            >
                              {/* Level Header */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.75rem',
                                  background: '#8b5cf6',
                                  color: 'white',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontWeight: 600 }}>Level {level.levelNumber}: {level.levelName}</span>
                                  <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                    ({level.approvers.length} approver{level.approvers.length !== 1 ? 's' : ''})
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    type="button"
                                    onClick={() => toggleLevelCollapse(level.id)}
                                    title={collapsedLevels.has(level.id) ? 'Expand' : 'Collapse'}
                                    style={{
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      border: '1px solid rgba(255, 255, 255, 0.3)',
                                      color: 'white',
                                      padding: '0.375rem',
                                      borderRadius: '0.25rem',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                  >
                                    {collapsedLevels.has(level.id) ? (
                                      <IconSystem.chevronDown size={16} />
                                    ) : (
                                      <IconSystem.chevronUp size={16} />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSequentialLevel(level.id)}
                                    disabled={isSubmitting}
                                    title="Remove level"
                                    style={{
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      border: '1px solid rgba(255, 255, 255, 0.3)',
                                      color: 'white',
                                      padding: '0.375rem',
                                      borderRadius: '0.25rem',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s',
                                      opacity: isSubmitting ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSubmitting) {
                                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                    }}
                                  >
                                    <IconSystem.trash size={16} />
                                  </button>
                                </div>
                              </div>

                              {/* Level Content */}
                              {!collapsedLevels.has(level.id) && (
                                <div style={{ padding: '1rem', background: 'white' }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => openApproverTable({ type: 'sequential-level', levelId: level.id })}
                                    disabled={isSubmitting}
                                    style={{ marginBottom: '0.75rem' }}
                                  >
                                    <IconSystem.add size={16} />
                                    Add Approvers to Level
                                  </button>

                                  {level.approvers.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      {level.approvers.map((approver) => (
                                        <div
                                          key={approver.id}
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.75rem',
                                            background: '#f8fafc',
                                            borderRadius: '0.375rem',
                                            border: '1px solid #e2e8f0',
                                          }}
                                        >
                                          <div>
                                            <div style={{ fontWeight: 500 }}>{approver.entityName}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                              {approver.role} • {approver.department} • {approver.branch}
                                              {approver.isAnyOneApprove && (
                                                <span style={{ marginLeft: '0.5rem', color: '#3b82f6' }}>
                                                  • Any one can approve
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            className="btn-icon"
                                            onClick={() => handleRemoveApproverFromLevel(level.id, approver.id)}
                                            disabled={isSubmitting}
                                            title="Remove approver"
                                          >
                                            <IconSystem.trash size={16} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        padding: '1rem',
                                        background: '#f8fafc',
                                        borderRadius: '0.375rem',
                                        border: '1px dashed #cbd5e1',
                                        color: '#64748b',
                                        fontSize: '0.875rem',
                                        textAlign: 'center',
                                      }}
                                    >
                                      Click "Add Approvers to Level" to add approvers
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: '1rem',
                            background: '#f8fafc',
                            borderRadius: '0.375rem',
                            border: '1px dashed #cbd5e1',
                            color: '#64748b',
                            fontSize: '0.875rem',
                            textAlign: 'center',
                          }}
                        >
                          No approval levels added yet
                        </div>
                      )}
                    </div>

                    {/* Final Approver */}
                    <div className="form-group">
                      <label className="form-label">
                        Final Approver <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      {formData.sequentialFinalApprover ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            background: '#f0fdf4',
                            borderRadius: '0.375rem',
                            border: '1px solid #bbf7d0',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 500 }}>{formData.sequentialFinalApprover.entityName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {formData.sequentialFinalApprover.role} • {formData.sequentialFinalApprover.department} • {formData.sequentialFinalApprover.branch}
                              {formData.sequentialFinalApprover.isAnyOneApprove && (
                                <span style={{ marginLeft: '0.5rem', color: '#3b82f6' }}>
                                  • Any one can approve
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleRemoveApprover('sequential-final')}
                            disabled={isSubmitting}
                            title="Remove final approver"
                          >
                            <IconSystem.trash size={16} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => openApproverTable({ type: 'sequential-final' })}
                          disabled={isSubmitting}
                        >
                          <IconSystem.add size={16} />
                          Add Final Approver
                        </button>
                      )}
                    </div>

                    {/* Sequential Flow Preview */}
                    {((formData.sequentialLevels && formData.sequentialLevels.length > 0) || (formData.requiresTwoLevelApproval && formData.preApprover)) && (
                      <ApprovalFlowPreview
                        flowType="sequential"
                        sequentialLevels={formData.sequentialLevels}
                        sequentialFinalApprover={formData.sequentialFinalApprover}
                        preApprover={formData.preApprover}
                        requiresTwoLevelApproval={formData.requiresTwoLevelApproval}
                      />
                    )}

                    {errors.approvalFlow && <div className="form-error" style={{ marginTop: '0.5rem' }}>{errors.approvalFlow}</div>}
                  </div>
                </div>
              )}

              {/* Tab 2: Basic Information */}
              {activeTab === 'basic' && (
                <div className="tab-panel">
                  {/* Basic Information */}
                  <div className="form-section" style={{ borderBottom: 'none' }}>
                    <h3 className="form-section-title">Basic Information</h3>

                    {/* Request Type - Searchable Dropdown */}
                    <div className="form-group">
                      <label className="form-label">
                        Request Type <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div style={{ position: 'relative' }}>
                        {/* Dropdown trigger button */}
                        <div
                          onClick={() => !isSubmitting && !loadingRequestTypes && setRequestTypeDropdownOpen(!requestTypeDropdownOpen)}
                          style={{
                            border: '1px solid #334155',
                            borderRadius: '0.375rem',
                            padding: '0.625rem 0.75rem',
                            background: isSubmitting || loadingRequestTypes ? '#f1f5f9' : 'white',
                            cursor: isSubmitting || loadingRequestTypes ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: '42px',
                          }}
                        >
                          <span style={{ color: formData.requestTypeId ? 'inherit' : '#64748b' }}>
                            {loadingRequestTypes
                              ? 'Loading request types...'
                              : formData.requestTypeId
                                ? requestTypes.find((rt: RequestTypeEntity) => rt.id === formData.requestTypeId)?.requestTypeName
                                : 'Select Request Type'}
                          </span>
                          <IconSystem.chevronDown
                            size={16}
                            style={{
                              transform: requestTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              color: '#64748b',
                            }}
                          />
                        </div>

                        {/* Dropdown menu */}
                        {requestTypeDropdownOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              marginTop: '4px',
                              background: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '0.375rem',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                              zIndex: 1000,
                              maxHeight: '300px',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            {/* Search input */}
                            <div style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                              <input
                                type="text"
                                placeholder="Search request types..."
                                value={requestTypeSearch}
                                onChange={(e) => setRequestTypeSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 0.75rem',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem',
                                  outline: 'none',
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#3b82f6';
                                  e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                            </div>

                            {/* Options list */}
                            <div style={{ overflow: 'auto', maxHeight: '240px' }}>
                              {filteredRequestTypes.length === 0 ? (
                                <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                                  No request types found
                                </div>
                              ) : (
                                filteredRequestTypes.map((type: RequestTypeEntity) => (
                                  <div
                                    key={type.id}
                                    onClick={() => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        requestTypeId: type.id,
                                        requestType: type.requestTypeName as RequestType,
                                      }));
                                      setRequestTypeDropdownOpen(false);
                                      setRequestTypeSearch('');
                                    }}
                                    style={{
                                      padding: '0.625rem 0.75rem',
                                      cursor: 'pointer',
                                      backgroundColor: formData.requestTypeId === type.id ? '#eff6ff' : 'transparent',
                                      borderLeft: formData.requestTypeId === type.id ? '3px solid #3b82f6' : '3px solid transparent',
                                      transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (formData.requestTypeId !== type.id) {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (formData.requestTypeId !== type.id) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <div style={{ fontWeight: formData.requestTypeId === type.id ? 600 : 400 }}>
                                      {type.requestTypeName}
                                    </div>
                                    {type.requestTypeCode && (
                                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                        {type.requestTypeCode}
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {/* Click outside handler */}
                        {requestTypeDropdownOpen && (
                          <div
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 999,
                            }}
                            onClick={() => {
                              setRequestTypeDropdownOpen(false);
                              setRequestTypeSearch('');
                            }}
                          />
                        )}
                      </div>
                      {requestTypesError && (
                        <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          Error loading request types. Using defaults.
                        </div>
                      )}
                      {errors.requestType && (
                        <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.requestType}
                        </div>
                      )}
                    </div>

                    {/* Priority */}
                    <div className="form-group">
                      <label className="form-label">
                        Priority <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value as RequestPriority)}
                        disabled={isSubmitting}
                        style={{ border: '1px solid #334155' }}
                      >
                        {PRIORITIES.map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div className="form-group">
                      <label className="form-label">
                        Subject <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="Enter subject (minimum 5 characters)"
                        disabled={isSubmitting}
                        style={{ border: errors.subject ? '1px solid #ef4444' : '1px solid #334155' }}
                      />
                      {errors.subject && <div className="form-error">{errors.subject}</div>}
                    </div>

                    {/* Description */}
                    <div className="form-group">
                      <label className="form-label">
                        Description <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea
                        className="form-textarea"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Enter detailed description (minimum 10 characters)"
                        rows={4}
                        maxLength={10000}
                        disabled={isSubmitting}
                        style={{
                          border: errors.description ? '1px solid #ef4444' : '1px solid #334155',
                          textAlign: 'justify'
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '4px'
                      }}>
                        {errors.description && <div className="form-error" style={{ margin: 0 }}>{errors.description}</div>}
                        <div style={{
                          marginLeft: 'auto',
                          fontSize: '12px',
                          color: formData.description.length > 9000 ? '#ef4444' : '#64748b'
                        }}>
                          {formData.description.length} / 10000
                        </div>
                      </div>
                    </div>

                    {/* Value */}
                    <div className="form-group">
                      <label className="form-label">
                        Value {selectedRequestType?.requiresValue
                          ? <span style={{ color: '#ef4444' }}>*</span>
                          : '(Optional)'}
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.value || ''}
                        onChange={(e) => handleInputChange('value', e.target.value ? parseFloat(e.target.value) : undefined)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="Enter value if applicable"
                        min="0"
                        step="0.01"
                        disabled={isSubmitting}
                        style={{ border: errors.value ? '1px solid #ef4444' : '1px solid #334155' }}
                      />
                      {errors.value && <div className="form-error">{errors.value}</div>}
                    </div>

                    {/* Required By Date */}
                    <div className="form-group">
                      <label className="form-label">Required By Date (Optional)</label>
                      <input
                        type="date"
                        className="form-input"
                        value={formData.requiredByDate || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, requiredByDate: e.target.value || undefined }))}
                        disabled={isSubmitting}
                        style={{ border: '1px solid #334155' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 3: Attachments */}
              {activeTab === 'attachments' && (
                <div className="tab-panel">
                  <div className="form-section" style={{ borderBottom: 'none' }}>
                    <h3 className="form-section-title">
                      Attachments
                      {selectedRequestType?.requiresAttachment
                        ? <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#ef4444', marginLeft: '0.5rem' }}>*Required</span>
                        : <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#64748b', marginLeft: '0.5rem' }}>(Optional)</span>
                      }
                    </h3>

                    {/* Attachment validation error */}
                    {errors.attachments && (
                      <div style={{
                        padding: '0.75rem 1rem',
                        background: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '0.375rem',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.8125rem',
                        color: '#ef4444',
                      }}>
                        <IconSystem.warning size={16} />
                        <span>{errors.attachments}</span>
                      </div>
                    )}

                    {/* File Size Limits Info */}
                    <div
                      style={{
                        padding: '0.75rem 1rem',
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '0.375rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.8125rem',
                        color: '#3b82f6',
                      }}
                    >
                      <IconSystem.info size={16} />
                      <span>
                        Max file size: <strong>{formatFileSize(fileSizeLimits.maxIndividualSize)}</strong> per file |
                        Total limit: <strong>{formatFileSize(fileSizeLimits.maxTotalSize)}</strong>
                        {fileSizeLimits.allowedExtensionsDisplay && (
                          <> | Allowed: <strong>{fileSizeLimits.allowedExtensionsDisplay}</strong></>
                        )}
                      </span>
                    </div>

                    {/* File Select — auto-adds to list immediately */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <input
                        id="file-upload"
                        type="file"
                        onChange={handleFileSelect}
                        disabled={isSubmitting}
                        accept={fileSizeLimits.allowedExtensions?.join(',') || undefined}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </div>

                    {/* Attachments List with inline editing */}
                    {formData.attachments && formData.attachments.length > 0 ? (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {formData.attachments.map((attachment, index) => (
                            <div
                              key={index}
                              style={{
                                padding: '0.75rem',
                                background: 'var(--bg-secondary, #f8fafc)',
                                borderRadius: '0.375rem',
                                border: '1px solid var(--border-color, #e2e8f0)',
                              }}
                            >
                              {/* Row 1: File info + action buttons */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <IconSystem.file size={14} style={{ color: 'var(--text-secondary, #64748b)' }} />
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>
                                    {attachment.file.name} ({formatFileSize(attachment.file.size)})
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  {/* View Button - Only for images and PDFs */}
                                  {(attachment.file.type.startsWith('image/') || attachment.file.type.includes('pdf')) && (
                                    <button
                                      type="button"
                                      onClick={() => handleViewAttachment(attachment)}
                                      disabled={isSubmitting}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--primary, #3b82f6)',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        padding: '0.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        opacity: isSubmitting ? 0.5 : 1,
                                      }}
                                      title="View attachment"
                                    >
                                      <IconSystem.eye size={16} />
                                    </button>
                                  )}
                                  {/* Delete Button */}
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleRemoveAttachment(index)}
                                    disabled={isSubmitting}
                                    title="Remove attachment"
                                  >
                                    <IconSystem.trash size={16} />
                                  </button>
                                </div>
                              </div>
                              {/* Row 2: Inline editable title + description */}
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Title *"
                                  value={attachment.title}
                                  onChange={(e) => handleUpdateAttachment(index, 'title', e.target.value)}
                                  disabled={isSubmitting}
                                  style={{ flex: 1, fontSize: '0.8125rem' }}
                                />
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Description (Optional)"
                                  value={attachment.description || ''}
                                  onChange={(e) => handleUpdateAttachment(index, 'description', e.target.value)}
                                  disabled={isSubmitting}
                                  style={{ flex: 1, fontSize: '0.8125rem' }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Total Size Summary */}
                        <div
                          style={{
                            marginTop: '0.75rem',
                            padding: '0.5rem 0.75rem',
                            background: 'var(--success-bg, #f0fdf4)',
                            border: '1px solid var(--success-border, #bbf7d0)',
                            borderRadius: '0.375rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.8125rem',
                          }}
                        >
                          <span style={{ color: 'var(--success-text, #15803d)' }}>
                            Total: {formData.attachments.length} file(s)
                          </span>
                          <span style={{ fontWeight: 600, color: 'var(--success-text, #15803d)' }}>
                            {getFormattedTotalSize(formData.attachments.map((a) => a.file))} / {formatFileSize(fileSizeLimits.maxTotalSize)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          padding: '1rem',
                          background: 'var(--bg-secondary, #f8fafc)',
                          borderRadius: '0.375rem',
                          border: '1px dashed var(--border-color, #cbd5e1)',
                          color: 'var(--text-secondary, #64748b)',
                          fontSize: '0.875rem',
                          textAlign: 'center',
                        }}
                      >
                        No attachments added — select a file above to attach it
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 4: Related Tickets */}
              {activeTab === 'related' && (
                <div className="tab-panel">
                  <div className="form-section" style={{ borderBottom: 'none' }}>
                    {/* Responsive two-column layout */}
                    <div className="related-tickets-grid">
                      {/* Search and Available Tickets */}
                      <div className="related-tickets-search">
                        <h3 className="form-section-title" style={{ marginTop: 0 }}>
                          Search & Link Requests
                          <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#64748b', marginLeft: '0.5rem' }}>
                            (Optional)
                          </span>
                        </h3>

                        {/* Search Related Tickets */}
                        <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Search by request number or subject..."
                            value={relatedTicketsSearch}
                            onChange={(e) => setRelatedTicketsSearch(e.target.value)}
                            disabled={isSubmitting || loadingRequests}
                            style={{ border: '1px solid #334155' }}
                          />
                        </div>

                        {/* Available Tickets List */}
                        <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '0.375rem' }}>
                          {loadingRequests ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>
                              Loading available requests...
                            </div>
                          ) : filteredTickets.length > 0 ? (
                            filteredTickets.map((ticket) => {
                              const isLinked = formData.relatedTickets?.includes(ticket.id) || false;
                              const statusColor = getRelatedTicketStatusColor(ticket?.currentStatus || '');
                              // Format date for display
                              const createdDate = ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              }) : '';
                              return (
                                <div
                                  key={ticket.id}
                                  onClick={() => handleToggleRelatedTicket(ticket.id)}
                                  style={{
                                    padding: '0.75rem',
                                    borderBottom: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    background: isLinked ? '#eff6ff' : 'transparent',
                                    transition: 'background 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLinked) {
                                      e.currentTarget.style.background = '#f8fafc';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = isLinked ? '#eff6ff' : 'transparent';
                                  }}
                                >
                                  {/* Header with Icon, Request Number, Request Type | Status + Action */}
                                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      {/* Icon */}
                                      <div
                                        style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '0.25rem',
                                          background: isLinked ? '#3b82f6' : '#94a3b8',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <IconSystem.link size={14} />
                                      </div>
                                      <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                                            {ticket.ticketNo}
                                          </span>
                                          {isLinked && (
                                            <span style={{
                                              fontSize: '0.6rem',
                                              background: '#3b82f6',
                                              color: 'white',
                                              padding: '0.1rem 0.25rem',
                                              borderRadius: '0.2rem'
                                            }}>
                                              Linked
                                            </span>
                                          )}
                                        </div>
                                        {ticket?.requestTypeName && (
                                          <div style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.2 }}>
                                            {ticket.requestTypeName}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                      {ticket?.currentStatus && (
                                        <div
                                          style={{
                                            padding: '0.125rem 0.375rem',
                                            borderRadius: '0.25rem',
                                            background: `${statusColor}20`,
                                            color: statusColor,
                                            fontSize: '0.6rem',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {ticket.currentStatus}
                                        </div>
                                      )}
                                      <div style={{ flexShrink: 0 }}>
                                        {isLinked ? (
                                          <IconSystem.check size={16} style={{ color: '#3b82f6' }} />
                                        ) : (
                                          <IconSystem.add size={16} style={{ color: '#94a3b8' }} />
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Subject */}
                                  <div style={{ fontSize: '0.8rem', color: '#1e293b', marginBottom: '0.375rem', lineHeight: 1.3 }}>
                                    {ticket.subject}
                                  </div>

                                  {/* Footer: Created By & Created Date */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem' }}>
                                    <div>
                                      <span style={{ color: '#64748b' }}>Created By: </span>
                                      <span style={{ color: '#1e293b', fontWeight: 500 }}>
                                        {ticket?.createdByName || 'Unknown'}
                                      </span>
                                    </div>
                                    {createdDate && (
                                      <div>
                                        <span style={{ color: '#64748b' }}>Created Date: </span>
                                        <span style={{ color: '#1e293b', fontWeight: 500 }}>
                                          {createdDate}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                              {relatedTicketsSearch ? 'No requests match your search' : 'No requests available to link'}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Linked Tickets Summary */}
                      <div className="related-tickets-linked">
                        <h3 className="form-section-title" style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <IconSystem.link size={16} />
                          Linked Requests
                          {formData.relatedTickets && formData.relatedTickets.length > 0 && (
                            <span style={{
                              fontSize: '0.75rem',
                              background: '#3b82f6',
                              color: 'white',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '1rem',
                              fontWeight: 600,
                            }}>
                              {formData.relatedTickets.length}
                            </span>
                          )}
                        </h3>

                        <div style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          minHeight: '120px',
                          maxHeight: '320px',
                          overflowY: 'auto',
                          background: '#fafafa',
                        }}>
                          {formData.relatedTickets && formData.relatedTickets.length > 0 ? (
                            <div style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {formData.relatedTickets.map((ticketId) => {
                                const ticket = availableTickets.find((t) => t.id === ticketId);
                                const statusColor = getRelatedTicketStatusColor(ticket?.currentStatus || '');
                                return (
                                  <div
                                    key={ticketId}
                                    style={{
                                      background: 'white',
                                      borderRadius: '0.375rem',
                                      padding: '0.75rem',
                                      border: '1px solid #e2e8f0',
                                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                    }}
                                  >
                                    {/* Header with Request Number and Status */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <div
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '0.25rem',
                                            background: '#3b82f6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <IconSystem.link size={16} />
                                        </div>
                                        <div>
                                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                                            {ticket?.ticketNo || ticketId}
                                          </div>
                                          {ticket?.requestTypeName && (
                                            <div style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.2 }}>
                                              {ticket.requestTypeName}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                        {ticket?.currentStatus && (
                                          <div
                                            style={{
                                              padding: '0.125rem 0.375rem',
                                              borderRadius: '0.25rem',
                                              background: `${statusColor}20`,
                                              color: statusColor,
                                              fontSize: '0.65rem',
                                              fontWeight: 600,
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {ticket.currentStatus}
                                          </div>
                                        )}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleRelatedTicket(ticketId);
                                          }}
                                          disabled={isSubmitting}
                                          title="Remove link"
                                          style={{
                                            background: '#fee2e2',
                                            border: 'none',
                                            borderRadius: '0.25rem',
                                            cursor: 'pointer',
                                            padding: '0.125rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <IconSystem.x size={12} style={{ color: '#dc2626' }} />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Subject */}
                                    {ticket?.subject && (
                                      <div style={{ marginBottom: '0.375rem' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#1e293b', lineHeight: 1.3 }}>
                                          {ticket.subject}
                                        </div>
                                      </div>
                                    )}

                                    {/* Details Grid - Created By & Created Date */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem' }}>
                                      <div>
                                        <span style={{ color: '#64748b' }}>Created By: </span>
                                        <span style={{ color: '#1e293b', fontWeight: 500 }}>
                                          {ticket?.createdByName || 'Unknown'}
                                        </span>
                                      </div>
                                      <div>
                                        <span style={{ color: '#64748b' }}>Created Date: </span>
                                        <span style={{ color: '#1e293b', fontWeight: 500 }}>
                                          {formatRelatedTicketDate(ticket?.createdAt)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '120px',
                              color: '#94a3b8',
                              textAlign: 'center',
                              padding: '1rem',
                            }}>
                              <IconSystem.link size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                              <div style={{ fontSize: '0.8rem' }}>No requests linked yet</div>
                              <div style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                Click on a request from the left to link it
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 5: Time Limit / SLA */}
              {activeTab === 'sla' && (
                <div className="tab-panel">
                  <div className="form-section" style={{ borderBottom: 'none' }}>
                    <h3 className="form-section-title">
                      Time Limit / SLA
                      <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#64748b', marginLeft: '0.5rem' }}>
                        (Optional)
                      </span>
                    </h3>

                    <div className="form-group">
                      <label className="form-label">SLA Hours</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.slaHours || ''}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value);
                          handleInputChange('slaHours', e.target.value && !isNaN(parsed) && parsed > 0 ? parsed : undefined);
                        }}
                        placeholder="Enter SLA in hours (e.g., 24, 48, 72)"
                        min="1"
                        disabled={isSubmitting}
                        style={{ border: '1px solid #334155' }}
                      />
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                        Specify the time limit for this request to be processed (in hours)
                      </div>
                    </div>

                    {formData.slaHours && !isNaN(formData.slaHours) && formData.slaHours > 0 && (
                      <div
                        style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          background: '#eff6ff',
                          borderRadius: '0.375rem',
                          border: '1px solid #bfdbfe',
                        }}
                      >
                        <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                          <IconSystem.clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                          This request must be resolved within <strong>{formData.slaHours} hours</strong>
                          {formData.slaHours >= 24 && ` (${Math.floor(formData.slaHours / 24)} day${Math.floor(formData.slaHours / 24) > 1 ? 's' : ''})`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <IconSystem.refresh size={16} className="spinner" />
                Creating...
              </>
            ) : (
              <>
                <IconSystem.check size={16} />
                Create Request
              </>
            )}
          </button>
        </div>
      </div>

      {/* Searchable Approver Table Modal */}
      {showApproverTable && (
        loadingApprovers ? (
          <div className="modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60
          }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '0.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Loading approvers...</div>
              <div style={{ color: '#64748b' }}>Please wait while we fetch available approvers</div>
            </div>
          </div>
        ) : (
          <SearchableApproverTable
            approvers={activeApprovers}
            onAddSelected={handleApproversSelected}
            onClose={() => setShowApproverTable(false)}
            excludeIds={getExcludedApproverIds()}
          />
        )
      )}

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            closePreview();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 11000,
            padding: '2rem',
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              color: 'white',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Attachment Preview</h3>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                {previewAttachment.fileName}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closePreview();
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Close preview"
            >
              <IconSystem.x size={24} />
            </button>
          </div>

          {/* Preview Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              background: 'white',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {previewAttachment.fileType.startsWith('image/') ? (
              <img
                src={previewAttachment.url}
                alt={previewAttachment.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : previewAttachment.fileType.includes('pdf') ? (
              <iframe
                src={previewAttachment.url}
                title={previewAttachment.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <IconSystem.file size={48} style={{ marginBottom: '1rem' }} />
                <p>Preview not available for this file type</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Centered message dialog (success / error / validation) */}
      <MessageDialog
        open={messageDialog !== null}
        variant={messageDialog?.variant}
        title={messageDialog?.title}
        message={messageDialog?.message || ''}
        onOk={() => messageDialog?.onOk()}
      />

      {/* Confirmation Dialog - Rendered via Portal */}
      {showConfirmDialog && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10060,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 10061,
              width: '90%',
              maxWidth: '500px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1.25rem 1.5rem',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderBottom: '1px solid #f59e0b',
              }}
            >
              <IconSystem.warning size={24} style={{ color: '#f59e0b' }} />
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#92400e' }}>
                Confirm Request Creation
              </h3>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>
                Are you sure you want to create this request?
              </p>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                }}
              >
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: '#64748b' }}>Type: </span>
                  <strong>{selectedRequestType?.requestTypeName || 'N/A'}</strong>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: '#64748b' }}>Subject: </span>
                  <strong>{formData.subject}</strong>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ color: '#64748b' }}>Priority: </span>
                  <strong>{formData.priority}</strong>
                </div>
                {formData.value !== undefined && formData.value !== null && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: '#64748b' }}>Value: </span>
                    <strong>{formData.value}</strong>
                  </div>
                )}
                {formData.attachments && formData.attachments.length > 0 && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: '#64748b' }}>Attachments: </span>
                    <strong>{formData.attachments.length} file(s)</strong>
                  </div>
                )}
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                  Approval: <strong>
                    {formData.workflowType === 'workflow' ? 'Predefined Workflow' :
                      formData.approvalFlowType === 'parallel' ? 'Parallel Approval' : 'Sequential Approval'}
                  </strong>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                padding: '1rem 1.5rem',
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <button
                type="button"
                onClick={() => setShowConfirmDialog(false)}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  background: '#3b82f6',
                  border: '1px solid #2563eb',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconSystem.check size={16} />
                  Confirm & Create
                </span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      <style>{`
        /* Tab Navigation Styles */
        .tab-navigation {
          display: flex;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem 0;
          border-bottom: 2px solid #e2e8f0;
          overflow-x: auto;
          /* Sticky positioning for scroll */
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: transparent;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          transition: all 0.2s;
          white-space: nowrap;
          position: relative;
        }

        .tab-button:hover {
          background: #f8fafc;
          color: #334155;
        }

        .tab-button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
          background: #eff6ff;
        }

        .tab-button.has-error {
          color: #ef4444;
        }

        .tab-button .error-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          background: #ef4444;
          color: white;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          margin-left: 0.25rem;
        }

        .tab-button .optional-label {
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 400;
        }

        /* Related Tickets - Responsive Grid Layout */
        .related-tickets-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        /* On smaller screens, stack vertically - Search first, then Linked (natural workflow order) */
        @media (max-width: 768px) {
          .related-tickets-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
        }

        .tab-content {
          padding: 1.5rem;
        }

        .tab-panel {
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Form Section Styles */
        .form-section {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .form-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-error {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .modal-xl {
          width: 90%;
          max-width: 1200px;
        }

        .attachment-form-grid {
          display: grid;
          grid-template-columns: repeat(9, 1fr);
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 900px) {
          .attachment-form-grid {
            grid-template-columns: 1fr;
          }

          .attachment-form-grid > div {
            grid-column: span 1 !important;
          }

          .tab-button span:not(.error-indicator):not(.optional-label) {
            display: none;
          }

          .tab-button {
            padding: 0.75rem;
          }
        }

        @media (min-width: 901px) and (max-width: 1100px) {
          .attachment-form-grid > div:nth-child(2),
          .attachment-form-grid > div:nth-child(3) {
            grid-column: span 4 !important;
          }
        }
      `}</style>
    </>
  );
};

export default NewTicketModal;
